/**
 * Custom hook for managing focus group configuration
 */
import { useState, useCallback, useEffect } from 'react';
import {
  saveConfiguration,
  getSavedConfigurations,
  loadConfiguration,
  deleteConfiguration,
  exportConfigurationToJSON,
  importConfigurationFromJSON,
  getPreferences,
} from '../utils/storage';
import { useTranslation } from '../i18n/LanguageContext';
import { useModels } from './ModelsContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Built-in demographic presets
export const DEMOGRAPHIC_PRESETS = [
  { id: 'general', name: 'Sveriges befolkning', description: 'Representativ fördelning av Sveriges demografi' },
  { id: 'urban', name: 'Storstadsbor', description: 'Fokus på Stockholm, Göteborg, Malmö' },
  { id: 'parents', name: 'Småbarnsföräldrar', description: 'Föräldrar med barn 0-12 år' },
  { id: 'seniors', name: 'Pensionärer', description: 'Aktiva 65+ i Sverige' },
  { id: 'students', name: 'Studenter', description: 'Universitetsstuderande 18-30' },
  { id: 'rural', name: 'Landsbygdsbor', description: 'Boende utanför storstäder' },
  { id: 'professionals', name: 'Yrkesverksamma', description: 'Heltidsanställda 25-55 år' },
  { id: 'tech', name: 'Tech-branchen', description: 'Arbetar inom IT och teknik' },
  { id: 'custom', name: 'Anpassad', description: 'Beskriv din målgrupp' },
];

export function useConfig() {
  const { t, language } = useTranslation();
  const { models, modelGroups, modelsLoading, resolveModel, resolveModelList } = useModels();
  const preferences = getPreferences();

  const [config, setConfig] = useState({
    title: '',
    topic: '',
    description: '',
    questions: [''],
    moderatorBriefing: '',
    moderatorDirection: '',
    moderatorAvoid: '',
    rounds: preferences.defaultRounds || 5,
    participantCount: preferences.defaultParticipants || 5,
    demographicPreset: 'general',
    customDemographic: '',
    moderatorModel: preferences.defaultModeratorModel || 'claude-sonnet-4.6',
    devilsAdvocate: false,
    enhancedBeliefTracking: false,
  });

  const [selectedModels, setSelectedModels] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [isGeneratingPersonas, setIsGeneratingPersonas] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState({});
  const [modelStatus, setModelStatus] = useState({});

  // Select all models once loaded from the API
  useEffect(() => {
    if (!modelsLoading && Object.keys(models).length > 0) {
      setSelectedModels(prev => prev.length === 0 ? Object.keys(models) : prev);
    }
  }, [models, modelsLoading]);

  // Load saved configurations on mount
  useEffect(() => {
    setSavedConfigs(getSavedConfigurations());
  }, []);

  // Check model availability on mount
  useEffect(() => {
    checkModelAvailability();
  }, []);

  // Check which models are available
  const checkModelAvailability = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/models/discover`);
      if (response.ok) {
        const data = await response.json();
        setModelStatus(data.configured_providers || {});
      }
    } catch (e) {
      console.error('Failed to check model availability:', e);
    }
  }, []);

  // Update config field
  const updateConfig = useCallback((field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  }, []);

  // Toggle individual model
  const toggleModel = useCallback((modelId) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(m => m !== modelId)
        : [...prev, modelId]
    );
  }, []);

  // Toggle entire group
  const toggleGroup = useCallback((group) => {
    const groupModelKeys = modelGroups[group] || [];
    const allSelected = groupModelKeys.every(m => selectedModels.includes(m));

    if (allSelected) {
      setSelectedModels(prev => prev.filter(m => !groupModelKeys.includes(m)));
    } else {
      setSelectedModels(prev => [...new Set([...prev, ...groupModelKeys])]);
    }
  }, [selectedModels, modelGroups]);

  // Check if group is fully selected
  const isGroupSelected = useCallback((group) => {
    return (modelGroups[group] || []).every(m => selectedModels.includes(m));
  }, [selectedModels, modelGroups]);

  // Check if group is partially selected
  const isGroupPartial = useCallback((group) => {
    const groupModelKeys = modelGroups[group] || [];
    const selectedCount = groupModelKeys.filter(m => selectedModels.includes(m)).length;
    return selectedCount > 0 && selectedCount < groupModelKeys.length;
  }, [selectedModels, modelGroups]);

  // Add question
  const addQuestion = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      questions: [...prev.questions, ''],
    }));
  }, []);

  // Update question
  const updateQuestion = useCallback((index, value) => {
    setConfig(prev => {
      const updated = [...prev.questions];
      updated[index] = value;
      return { ...prev, questions: updated };
    });
  }, []);

  // Remove question
  const removeQuestion = useCallback((index) => {
    setConfig(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  }, []);

  // Generate personas
  const generatePersonas = useCallback(async () => {
    if (selectedModels.length === 0) {
      return { error: t('errors.selectModel') };
    }

    setIsGeneratingPersonas(true);

    try {
      const response = await fetch(`${API_BASE}/personas/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: config.demographicPreset === 'custom' ? config.customDemographic : null,
          use_swedish_demographics: config.demographicPreset !== 'custom',
          count: config.participantCount,
          allowed_models: selectedModels,
          language: language,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate personas');

      const data = await response.json();
      setPersonas(data.personas);
      return { success: true };
    } catch (err) {
      console.error(err);
      return { error: t('errors.generateFailed') };
    } finally {
      setIsGeneratingPersonas(false);
    }
  }, [config.demographicPreset, config.customDemographic, config.participantCount, selectedModels, language, t]);

  // Update single persona
  const updatePersona = useCallback((personaId, updates) => {
    setPersonas(prev =>
      prev.map(p => p.id === personaId ? { ...p, ...updates } : p)
    );
  }, []);

  // Remove persona
  const removePersona = useCallback((personaId) => {
    setPersonas(prev => prev.filter(p => p.id !== personaId));
  }, []);

  // Regenerate single persona
  const regeneratePersona = useCallback(async (personaId) => {
    const persona = personas.find(p => p.id === personaId);
    if (!persona) return { error: t('errors.personaNotFound') };

    try {
      const response = await fetch(`${API_BASE}/personas/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          description: config.demographicPreset === 'custom' ? config.customDemographic : null,
          use_swedish_demographics: config.demographicPreset !== 'custom',
          count: 1,
          allowed_models: [persona.model_id],
          language: language,
        }),
      });

      if (!response.ok) throw new Error('Failed to regenerate persona');

      const data = await response.json();
      if (data.personas && data.personas[0]) {
        setPersonas(prev =>
          prev.map(p => p.id === personaId ? { ...data.personas[0], id: personaId } : p)
        );
        return { success: true };
      }
    } catch (err) {
      console.error(err);
      return { error: t('errors.regenerateFailed') };
    }
  }, [personas, config.demographicPreset, config.customDemographic, language, t]);

  // Save current configuration
  const saveCurrentConfig = useCallback((name) => {
    const id = saveConfiguration(name, config, selectedModels, personas);
    setSavedConfigs(getSavedConfigurations());
    return id;
  }, [config, selectedModels, personas]);

  // Load a saved configuration, remapping any deprecated models to active replacements
  const loadSavedConfig = useCallback((id) => {
    const saved = loadConfiguration(id);
    if (saved) {
      const loadedConfig = { ...saved.config };
      if (loadedConfig.moderatorModel) {
        loadedConfig.moderatorModel = resolveModel(loadedConfig.moderatorModel);
      }
      setConfig(loadedConfig);
      setSelectedModels(resolveModelList(saved.selectedModels) || Object.keys(models));
      if (saved.personas) {
        setPersonas(saved.personas.map(p => ({
          ...p,
          model_id: resolveModel(p.model_id),
        })));
      }
      return true;
    }
    return false;
  }, [models, resolveModel, resolveModelList]);

  // Delete a saved configuration
  const deleteSavedConfig = useCallback((id) => {
    deleteConfiguration(id);
    setSavedConfigs(getSavedConfigurations());
  }, []);

  // Export configuration
  const exportConfig = useCallback((id) => {
    exportConfigurationToJSON(id);
  }, []);

  // Import configuration
  const importConfig = useCallback(async (file) => {
    try {
      const result = await importConfigurationFromJSON(file);
      setSavedConfigs(getSavedConfigurations());
      return result;
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Reset configuration
  const resetConfig = useCallback(() => {
    setConfig({
      title: '',
      topic: '',
      description: '',
      questions: [''],
      moderatorBriefing: '',
      moderatorDirection: '',
      moderatorAvoid: '',
      rounds: 5,
      participantCount: 5,
      demographicPreset: 'general',
      customDemographic: '',
      moderatorModel: 'claude-sonnet-4.6',
      devilsAdvocate: false,
      enhancedBeliefTracking: false,
    });
    setPersonas([]);
  }, []);

  return {
    // State
    config,
    selectedModels,
    personas,
    isGeneratingPersonas,
    savedConfigs,
    modelStatus,
    models,
    modelGroups,
    modelsLoading,
    resolveModel,

    // Config actions
    updateConfig,
    setConfig,
    resetConfig,

    // Model actions
    setSelectedModels,
    toggleModel,
    toggleGroup,
    isGroupSelected,
    isGroupPartial,
    checkModelAvailability,

    // Question actions
    addQuestion,
    updateQuestion,
    removeQuestion,

    // Persona actions
    setPersonas,
    generatePersonas,
    updatePersona,
    removePersona,
    regeneratePersona,

    // Save/Load actions
    saveCurrentConfig,
    loadSavedConfig,
    deleteSavedConfig,
    exportConfig,
    importConfig,
  };
}
